import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";

import { getCitationContextGraph, GraphNode, GraphPayload, GraphSourceOrigin } from "../../api";
import { ClinicalMapRenderer } from "./ClinicalMapRenderer";
import styles from "./EvidenceGraph.module.css";
import { GraphExplorerRenderer } from "./GraphExplorerRenderer";
import {
    countSourceContributions,
    SOURCE_OPTIONS,
    sourceMatchesSelectedFilters,
    toggleOrigin,
    withSemanticCitationOrigins
} from "./graphPresentation";

type RendererMode = "clinicalMap" | "graphExplorer";

interface Props {
    assessmentIds: string[];
    idToken?: string;
    onCitationClicked?: (citationFilePath: string) => void;
}

export const EvidenceGraph = ({ assessmentIds, idToken, onCitationClicked }: Props) => {
    const navigate = useNavigate();
    const [payload, setPayload] = useState<GraphPayload | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedOrigins, setSelectedOrigins] = useState<Set<GraphSourceOrigin>>(new Set(SOURCE_OPTIONS.map(option => option.key)));
    const [graphAddedOnly, setGraphAddedOnly] = useState(false);
    const [rendererMode, setRendererMode] = useState<RendererMode>("clinicalMap");

    useEffect(() => {
        let cancelled = false;
        if (!assessmentIds.length) {
            setPayload(null);
            setError(null);
            return;
        }
        setIsLoading(true);
        setError(null);
        getCitationContextGraph(assessmentIds, idToken)
            .then(result => {
                if (!cancelled) {
                    setPayload(withSemanticCitationOrigins(result, assessmentIds));
                }
            })
            .catch(fetchError => {
                if (!cancelled) {
                    setPayload(null);
                    setError(fetchError instanceof Error ? fetchError.message : "Unable to load evidence graph.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [assessmentIds.join("|"), idToken]);

    const visiblePayload = useMemo(() => {
        if (!payload) {
            return null;
        }
        const nodeIds = new Set<string>();
        const nodes = payload.nodes.filter(node => {
            const origin = node.source_origin ?? "context_graph";
            const isVisible = graphAddedOnly ? origin === "context_graph" : sourceMatchesSelectedFilters(origin, selectedOrigins);
            if (isVisible) {
                nodeIds.add(node.id);
            }
            return isVisible;
        });
        const edges = payload.edges.filter(edge => {
            const origin = edge.source_origin ?? "context_graph";
            const sourceVisible = nodeIds.has(edge.source);
            const targetVisible = nodeIds.has(edge.target);
            const originVisible = graphAddedOnly ? origin === "context_graph" : sourceMatchesSelectedFilters(origin, selectedOrigins);
            return originVisible && sourceVisible && targetVisible;
        });
        return { ...payload, nodes, edges };
    }, [payload, selectedOrigins, graphAddedOnly]);

    const originCounts = useMemo(() => countSourceContributions(payload), [payload]);
    const patientGraphTarget = useMemo(() => getPatientGraphTarget(payload, assessmentIds), [payload, assessmentIds]);

    if (!assessmentIds.length) {
        return <EmptyState title="No clinical citations" message="Evidence graph is available after an answer cites clinical notes." />;
    }

    if (isLoading) {
        return (
            <div className={styles.loadingState}>
                <Spinner size={SpinnerSize.medium} label="Loading evidence graph" />
            </div>
        );
    }

    if (error) {
        return <EmptyState title="Evidence graph unavailable" message={error} />;
    }

    if (!visiblePayload || !payload?.metadata?.found) {
        return <EmptyState title="No graph evidence found" message="The cited notes did not return bounded graph context." />;
    }

    return (
        <div className={styles.evidenceGraph}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarGroup} aria-label="Evidence source filters">
                    {SOURCE_OPTIONS.map(option => {
                        const isActive = selectedOrigins.has(option.key) && !graphAddedOnly;
                        return (
                            <button
                                key={option.key}
                                type="button"
                                className={`${styles.sourceChip} ${styles[`source_${option.key}`]} ${isActive ? styles.sourceChipActive : ""}`}
                                onClick={() => setSelectedOrigins(toggleOrigin(selectedOrigins, option.key))}
                                disabled={graphAddedOnly}
                            >
                                {option.label} {originCounts[option.key] ? `(${originCounts[option.key]})` : ""}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        className={`${styles.sourceChip} ${styles.graphAddedOnly} ${graphAddedOnly ? styles.sourceChipActive : ""}`}
                        onClick={() => setGraphAddedOnly(!graphAddedOnly)}
                    >
                        Graph-added only
                    </button>
                </div>
                <div className={styles.rendererSwitch} aria-label="Evidence graph view">
                    <DefaultButton
                        text="Clinical Map"
                        onClick={() => setRendererMode("clinicalMap")}
                        primary={rendererMode === "clinicalMap"}
                    />
                    <DefaultButton
                        text="Graph Explorer"
                        onClick={() => setRendererMode("graphExplorer")}
                        primary={rendererMode === "graphExplorer"}
                    />
                    {patientGraphTarget && (
                        <DefaultButton
                            text="Open Patient Graph"
                            onClick={() => navigate(patientGraphTarget)}
                        />
                    )}
                </div>
            </div>

            <div className={styles.summaryRow}>
                <span>{visiblePayload.nodes.length} items</span>
                <span>{visiblePayload.edges.length} connections</span>
                <span>{assessmentIds.length} cited notes requested</span>
            </div>

            {rendererMode === "clinicalMap" ? (
                <ClinicalMapRenderer payload={visiblePayload} onCitationClicked={onCitationClicked} />
            ) : (
                <GraphExplorerRenderer payload={visiblePayload} onCitationClicked={onCitationClicked} />
            )}
        </div>
    );
};

function EmptyState({ title, message }: { title: string; message: string }) {
    return (
        <div className={styles.emptyState}>
            <h3>{title}</h3>
            <p>{message}</p>
        </div>
    );
}


function getPatientGraphTarget(payload: GraphPayload | null, assessmentIds: string[]): string | null {
    if (!payload) {
        return null;
    }

    const patientNode = payload.nodes.find(node => isPatientNode(node));
    const focusNode =
        payload.nodes.find(node => node.assessment_id && assessmentIds.includes(node.assessment_id)) ??
        payload.nodes.find(node => node.assessment_id) ??
        patientNode ??
        payload.nodes.find(node => node.consumer_id);
    const consumerId = focusNode?.consumer_id ?? patientNode?.consumer_id ?? getMetadataString(payload, "consumer_id");

    if (!consumerId) {
        return null;
    }

    const params = new URLSearchParams();
    params.set("consumerId", consumerId);
    const patientName = patientNode?.label;
    if (patientName) {
        params.set("patientName", patientName);
    }
    if (focusNode?.id) {
        params.set("graphFocusId", focusNode.id);
    }
    if (focusNode?.assessment_id) {
        params.set("assessmentId", focusNode.assessment_id);
    }
    params.set("graphDepth", "1");

    return `/trajectory?${params.toString()}`;
}

function isPatientNode(node: GraphNode): boolean {
    const type = (node.clinical_type || node.type || "").toLowerCase();
    return type.includes("patient");
}

function getMetadataString(payload: GraphPayload, key: string): string | undefined {
    const value = payload.metadata?.[key];
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
}
